import { AnalyticsModuleBootstrapError, logger } from "../debug";
import { getCookie } from "./cookies";
import { getSpecificRegion, isESWRegion } from "./envFns";

const intlConsentCookieDomainMap: Array<{
  hostSuffix: string;
  cookieDomain: string;
}> = [
  { hostSuffix: ".lululemon.com", cookieDomain: ".lululemon.com" },
  { hostSuffix: ".lululemon.mx", cookieDomain: ".lululemon.mx" },
  {
    hostSuffix: ".sandbox.eshopworld.com",
    cookieDomain: ".sandbox.eshopworld.com",
  },
  {
    hostSuffix: ".nonprod-lululemon.com",
    cookieDomain: ".nonprod-lululemon.com",
  },
];

function getIntlConsentCookieDomain(hostname: string): string | null {
  const domainConfig = intlConsentCookieDomainMap.find(({ hostSuffix }) =>
    hostname.endsWith(hostSuffix)
  );
  return domainConfig?.cookieDomain ?? null;
}

/**
 * Sets the international OneTrust consent cookie
 */
export function setIntlOTConsentCookie(): void {
  const country = getSpecificRegion();
  const isESWRegionValue = isESWRegion(country);

  if (!isESWRegionValue) {
    logger.warn(
      "Not setting international OneTrust consent cookie as site is not ESW"
    );
    return;
  }

  if (typeof window === "undefined") {
    logger.warn("Window is not available for setting consent cookie");
    return;
  }

  try {
    const cookieName = "intlOTConsent";
    let activeGroups: string[] = [];

    // Primary: Get consent data from OneTrust's in-memory API
    if (window.OnetrustActiveGroups) {
      // OnetrustActiveGroups contains comma-separated active group IDs like ",C0001,C0002,C0003,"
      activeGroups = window.OnetrustActiveGroups.split(",").filter((g) =>
        g.trim()
      );
    } else {
      // Fallback: Parse from OptanonConsent cookie
      logger.debug(
        "OnetrustActiveGroups not available, falling back to cookie"
      );
      const optanonConsentValue = getCookie("OptanonConsent");

      if (!optanonConsentValue) {
        logger.warn(
          "Neither OnetrustActiveGroups nor OptanonConsent cookie available"
        );
        return;
      }

      const groupsMatch = optanonConsentValue.match(/groups=([^&]+)/);

      if (groupsMatch && groupsMatch[1]) {
        const groupsValue = groupsMatch[1];
        // Parse groups like "C0004:0,C0003:1,C0002:1,C0001:1" and filter only active ones
        activeGroups = groupsValue
          .split(",")
          .filter((group) => group.endsWith(":1"))
          .map((group) => group.split(":")[0]);
      } else {
        logger.warn("Could not parse groups from OptanonConsent cookie");
        return;
      }
    }

    // Build consent values for C0001-C0004 with :1 if active, :0 if not
    const consentCookieVal = [1, 2, 3, 4].reduce<string[]>((acc, i) => {
      const groupId = `C000${i}`;
      const isActive = activeGroups.includes(groupId) ? "1" : "0";
      acc.push(`${groupId}:${isActive}`);
      return acc;
    }, []);

    // Validate we have consent values
    if (consentCookieVal.length === 0) {
      logger.warn("No consent values found");
      return;
    }

    const date = new Date();
    // Set the expiry date to one year from the current date
    date.setFullYear(date.getFullYear() + 1);
    const expires = `; expires=${date.toUTCString()}`;

    const cookieDomain = getIntlConsentCookieDomain(window.location.hostname);
    if (!cookieDomain) {
      logger.warn(
        `Could not resolve consent cookie domain for hostname: ${window.location.hostname}`
      );
      return;
    }

    document.cookie = `${cookieName}=${consentCookieVal.join(",")}${expires}; path=/; domain=${cookieDomain}; Secure; SameSite=Lax`;

    logger.debug("International OneTrust consent cookie set successfully");
  } catch (error) {
    logger.error(
      new AnalyticsModuleBootstrapError(
        "Failed to set international OneTrust consent cookie",
        { cause: error }
      )
    );
  }
}
